import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { useUserStore } from '../stores/useUserStore';
import { configs } from '../configs';
import {
  authenticatedApiCall,
  authenticatedPost,
} from '../utils/authenticatedFetch';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  ShoppingBag,
  Gift,
  Star,
  Clock,
  CheckCircle,
  XCircle,
  Filter,
  Search,
  Award,
  Coffee,
  Calendar,
  User,
} from 'lucide-react';

interface RedemptionItem {
  id: string;
  name: string;
  description: string;
  pointsRequired: number;
  category: string;
  imageUrl?: string;
  isAvailable: boolean;
  stock: number;
  expiryDate?: string;
}

interface RedemptionRequest {
  id: string;
  itemId: string;
  itemName: string;
  pointsUsed: number;
  status: 'pending' | 'approved' | 'rejected' | 'fulfilled';
  requestDate: string;
  approvalDate?: string;
  approvedBy?: string;
  notes?: string;
}

interface RedemptionHistory {
  id: string;
  itemName: string;
  pointsUsed: number;
  status: string;
  date: string;
  notes?: string;
}

const ModernPointRedemption: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { user } = useUserStore();

  const [items, setItems] = useState<RedemptionItem[]>([]);
  const [requests, setRequests] = useState<RedemptionRequest[]>([]);
  const [history, setHistory] = useState<RedemptionHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentPoints, setCurrentPoints] = useState(0);
  const [selectedItem, setSelectedItem] = useState<RedemptionItem | null>(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestNotes, setRequestNotes] = useState('');

  useEffect(() => {
    loadItems();
    loadRequests();
    loadHistory();
    loadCurrentPoints();
  }, []);

  const loadItems = async () => {
    setLoading(true);
    try {
      // Fetch redemption items from API
      const response = await fetch(`${configs.apiRoot}/redemption_items`, {
        headers: {
          'Content-Type': 'application/json',
          // Add auth header if needed
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch redemption items: ${response.statusText}`
        );
      }

      const apiItems = await response.json();

      // Transform API data to component format
      const items: RedemptionItem[] = apiItems.map((item: any) => ({
        id: item._id,
        name: item.name,
        description: item.description,
        pointsRequired: item.pointsRequired,
        category: item.category,
        isAvailable: item.isAvailable,
        stock: item.stock,
        imageUrl: item.imageUrl,
        expiryDate: item.expiryDate,
      }));

      setItems(items);
    } catch (error) {
      console.error('Error loading items:', error);
      notify('Failed to load redemption items', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadRequests = async () => {
    try {
      // Fetch redemption requests from API with authentication
      const apiRequests = await authenticatedApiCall<any[]>(
        `${configs.apiRoot}/redemption_requests?userId=${user?.id}`
      );

      // Transform API data to component format
      const requests: RedemptionRequest[] = apiRequests.map((request: any) => ({
        id: request._id,
        itemId: request.itemId,
        itemName: request.itemName,
        pointsUsed: request.pointsUsed,
        status: request.status,
        requestDate: request.requestDate,
        approvalDate: request.approvalDate,
        approvedBy: request.approvedBy,
        notes: request.notes,
      }));

      setRequests(requests);
    } catch (error) {
      console.error('Error loading requests:', error);
    }
  };

  const loadHistory = async () => {
    try {
      // Fetch redemption history from API
      const response = await fetch(
        `${configs.apiRoot}/redemption_history?userId=${user?.id}`,
        {
          headers: {
            'Content-Type': 'application/json',
            // Add auth header if needed
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch redemption history: ${response.statusText}`
        );
      }

      const apiHistory = await response.json();

      // Transform API data to component format
      const history: RedemptionHistory[] = apiHistory.map((item: any) => ({
        id: item._id,
        itemName: item.itemName,
        pointsUsed: item.pointsUsed,
        status: item.status,
        date: item.date,
        notes: item.notes,
      }));

      setHistory(history);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const loadCurrentPoints = async () => {
    try {
      // Fetch current points from API
      const response = await fetch(
        `${configs.apiRoot}/staff_points?userId=${user?.id}`,
        {
          headers: {
            'Content-Type': 'application/json',
            // Add auth header if needed
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch current points: ${response.statusText}`
        );
      }

      const apiPoints = await response.json();
      const currentPoints =
        apiPoints.length > 0 ? apiPoints[0].currentPoints : 0;
      setCurrentPoints(currentPoints);
    } catch (error) {
      console.error('Error loading current points:', error);
    }
  };

  const filteredItems = items.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      categoryFilter === 'all' || item.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Beverages':
        return <Coffee className="h-4 w-4" />;
      case 'Food':
        return <Gift className="h-4 w-4" />;
      case 'Time Off':
        return <Calendar className="h-4 w-4" />;
      case 'Gift Cards':
        return <ShoppingBag className="h-4 w-4" />;
      case 'Products':
        return <Award className="h-4 w-4" />;
      case 'Team Events':
        return <User className="h-4 w-4" />;
      default:
        return <Star className="h-4 w-4" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'fulfilled':
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'fulfilled':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleRequestItem = (item: RedemptionItem) => {
    if (currentPoints < item.pointsRequired) {
      notify('Insufficient points for this item', { type: 'error' });
      return;
    }

    setSelectedItem(item);
    setShowRequestModal(true);
  };

  const submitRequest = async () => {
    if (!selectedItem) return;

    try {
      // Mock API call - replace with actual implementation
      const newRequest: RedemptionRequest = {
        id: Date.now().toString(),
        itemId: selectedItem.id,
        itemName: selectedItem.name,
        pointsUsed: selectedItem.pointsRequired,
        status: 'pending',
        requestDate: new Date().toISOString(),
        notes: requestNotes,
      };

      setRequests((prev) => [newRequest, ...prev]);
      setCurrentPoints((prev) => prev - selectedItem.pointsRequired);
      setShowRequestModal(false);
      setRequestNotes('');
      setSelectedItem(null);

      notify('Redemption request submitted successfully!', { type: 'success' });
    } catch (error) {
      console.error('Error submitting request:', error);
      notify('Failed to submit request', { type: 'error' });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading redemption items...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      <div className="flex flex-col items-start">
        <h1 className="text-3xl font-bold text-foreground">Point Redemption</h1>
        <p className="text-muted-foreground mt-2">
          Redeem your points for rewards and benefits
        </p>
      </div>

      {/* Current Points */}
      <Card className="border-primary bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-primary" />
            Your Points Balance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-primary">
            {currentPoints.toLocaleString()} points
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Available for redemption
          </p>
        </CardContent>
      </Card>

      {/* Search and Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Search & Filter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label htmlFor="search">Search items</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search redemption items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="ml-2 px-3 py-2 border rounded-md"
              >
                <option value="all">All Categories</option>
                <option value="Beverages">Beverages</option>
                <option value="Food">Food</option>
                <option value="Time Off">Time Off</option>
                <option value="Gift Cards">Gift Cards</option>
                <option value="Products">Products</option>
                <option value="Team Events">Team Events</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Redemption Items */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredItems.map((item) => (
          <Card key={item.id} className="relative">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  {getCategoryIcon(item.category)}
                  <CardTitle className="text-lg">{item.name}</CardTitle>
                </div>
                <Badge variant="outline" className="flex items-center gap-1">
                  <Star className="h-3 w-3" />
                  {item.pointsRequired}
                </Badge>
              </div>
              <CardDescription>{item.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span>Stock: {item.stock}</span>
                  <span
                    className={
                      item.isAvailable ? 'text-green-600' : 'text-red-600'
                    }
                  >
                    {item.isAvailable ? 'Available' : 'Out of Stock'}
                  </span>
                </div>

                <Button
                  onClick={() => handleRequestItem(item)}
                  disabled={
                    !item.isAvailable || currentPoints < item.pointsRequired
                  }
                  className="w-full"
                >
                  {currentPoints < item.pointsRequired
                    ? 'Insufficient Points'
                    : 'Request Item'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Current Requests */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Your Requests
          </CardTitle>
          <CardDescription>Track your redemption requests</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {requests.map((request) => (
              <div
                key={request.id}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {getStatusIcon(request.status)}
                  <div>
                    <div className="font-semibold">{request.itemName}</div>
                    <div className="text-sm text-muted-foreground">
                      {request.pointsUsed} points •{' '}
                      {formatDate(request.requestDate)}
                    </div>
                  </div>
                </div>
                <Badge className={getStatusColor(request.status)}>
                  {request.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Redemption History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Redemption History
          </CardTitle>
          <CardDescription>Your past redemptions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {history.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div>
                  <div className="font-semibold">{item.itemName}</div>
                  <div className="text-sm text-muted-foreground">
                    {item.pointsUsed} points • {formatDate(item.date)}
                  </div>
                </div>
                <Badge variant="outline" className="text-green-600">
                  {item.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Request Modal */}
      {showRequestModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Request {selectedItem.name}</CardTitle>
              <CardDescription>
                This will cost {selectedItem.pointsRequired} points
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Add any notes for your manager..."
                  value={requestNotes}
                  onChange={(e) => setRequestNotes(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={submitRequest} className="flex-1">
                  Submit Request
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRequestModal(false);
                    setSelectedItem(null);
                    setRequestNotes('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ModernPointRedemption;
